import { computed, ref } from 'vue'

/**
 * Instalação da PWA ("Instalar app" no menu do perfil).
 *
 * O Chrome/Edge disparam `beforeinstallprompt` uma vez, cedo, às vezes antes do
 * `app.mount`. Por isso o listener é registrado no import do módulo (main.ts
 * importa este arquivo) e o evento fica guardado aqui até alguém chamar
 * `promptInstall`. Safari não dispara o evento: lá a instalação é pelo
 * "Adicionar à Tela de Início" e o item simplesmente não aparece.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
const installed = ref(false)

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  const nav = window.navigator as Navigator & { standalone?: boolean }
  return Boolean(window.matchMedia?.('(display-mode: standalone)').matches || nav.standalone)
}

export function listenForInstallPrompt(target: Window = window): void {
  installed.value = isStandalone()

  target.addEventListener('beforeinstallprompt', (event: Event) => {
    // Sem o preventDefault o navegador mostra a própria barra; queremos o item do menu.
    event.preventDefault()
    deferredPrompt.value = event as BeforeInstallPromptEvent
  })

  target.addEventListener('appinstalled', () => {
    installed.value = true
    deferredPrompt.value = null
  })
}

/** Só para os testes: zera o estado compartilhado do módulo. */
export function resetPwaInstallState(): void {
  deferredPrompt.value = null
  installed.value = false
}

export function usePwaInstall() {
  const canInstall = computed(() => !installed.value && deferredPrompt.value !== null)

  async function promptInstall(): Promise<boolean> {
    const event = deferredPrompt.value
    if (!event) return false
    // O evento só pode ser usado uma vez, aceito ou não.
    deferredPrompt.value = null
    await event.prompt()
    const { outcome } = await event.userChoice
    if (outcome === 'accepted') installed.value = true
    return outcome === 'accepted'
  }

  return { canInstall, installed, promptInstall }
}
